import {
  WebTracerProvider,
  BatchSpanProcessor,
  type Span,
  type SpanProcessor,
} from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { Resource } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import {
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
  ATTR_SESSION_ID,
  ATTR_OS_NAME,
  ATTR_OS_VERSION,
} from '@opentelemetry/semantic-conventions/incubating';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { metrics, trace, SpanStatusCode, type Context, type Exception } from '@opentelemetry/api';
import { onCLS, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { env } from '@/env';

let isInitialized = false;

/** Named, independently-isolated initialization step — see `runInitSteps`. */
interface InitStep {
  readonly name: string;
  readonly run: () => void;
}

/**
/**
 * Boot browser OpenTelemetry.
 *
 * Registers the auto-instrumentations for document load, fetch/XHR, and
 * user interactions; exports OTLP/HTTP to the gateway behind CloudFront.
 * Web Vitals are emitted as OTel metrics (LCP, INP, CLS, TTFB — FID is
 * intentionally omitted: web-vitals has deprecated it in favor of INP, which
 * is already captured), and uncaught errors/unhandled rejections are
 * captured (a low-cardinality counter for rate + a recordException span for
 * the full detail) so this SDK's coverage matches what Coralogix RUM's own
 * instrumentation captures.
 *
 * Also attaches RUM-style context so this data can be sliced the way
 * Coralogix RUM's own data can be: a tab-scoped session id and parsed
 * browser/OS (both static for the session, attached once via the shared
 * `Resource` so every span *and* metric gets them for free), and the
 * current SPA route (`page.path`, dynamic — tracked via patched
 * `history.pushState`/`replaceState` + `popstate`, since this is a
 * client-routed app and most spans/metrics fire without a hard page load).
 * `page.path` is normalized (UUID/numeric segments → `:id`) so it stays a
 * bounded route pattern rather than one distinct value per record visited,
 * which would otherwise blow up metric cardinality.
 *
 * Each step below runs behind its own error boundary (see `runInitSteps`),
 * so a single misbehaving step (e.g. a blocked OTLP endpoint, an instrumentation
 * that throws in an unusual browser) never prevents the others from
 * registering, and — critically — never throws back into `main.tsx`'s
 * bootstrap and blocks the app from rendering.
 *
 * Idempotent — safe to call from `main.tsx` and tests.
 */
export const setupWebTelemetry = (): void => {
  if (isInitialized) return;
  isInitialized = true;

  // No collector configured — skip the SDK entirely instead of letting
  // OTLP exporters spam the console with ERR_CONNECTION_REFUSED at the
  // localhost default. Re-enable per env by setting VITE_OTEL_ENDPOINT.
  if (!env.otel.endpoint) return;

  // Both helpers are internally fault-tolerant (never throw): parseUserAgent
  // is pure regex matching over a string, and getOrCreateSessionId catches
  // its own storage/crypto access. Safe to call ahead of `runInitSteps`,
  // since their results feed the `Resource` those steps are built from.
  const sessionId = getOrCreateSessionId();
  const { browserName, browserVersion, osName, osVersion } = parseUserAgent(navigator.userAgent);

  const resource = new Resource({
    [ATTR_SERVICE_NAME]: env.otel.serviceName,
    [ATTR_SERVICE_VERSION]: env.otel.serviceVersion,
    [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: env.appEnv,
    [ATTR_SESSION_ID]: sessionId,
    [ATTR_OS_NAME]: osName,
    [ATTR_OS_VERSION]: osVersion,
    'browser.name': browserName,
    'browser.version': browserVersion,
  });

  runInitSteps([
    { name: 'page-tracking', run: setupPageTracking },
    { name: 'trace-provider', run: () => setupTraceProvider(resource) },
    { name: 'meter-provider', run: () => setupMeterProvider(resource) },
    { name: 'auto-instrumentations', run: setupAutoInstrumentations },
    { name: 'web-vitals', run: setupWebVitalsMetrics },
    { name: 'error-capture', run: setupErrorCapture },
  ]);
};

/**
 * Runs each step behind its own try/catch so one failure can't take down
 * the rest of telemetry setup (or, if left unguarded, the whole app render).
 */
const runInitSteps = (steps: readonly InitStep[]): void => {
  for (const step of steps) {
    try {
      step.run();
    } catch (error) {
      reportSetupFailure(step.name, error);
    }
  }
};

const SESSION_STORAGE_KEY = 'qfly.otel.session_id';

/**
 * Tab-scoped RUM diagnostics session id — generated once and kept for the
 * life of the tab (sessionStorage; cleared on tab close). Deliberately NOT
 * an attempt at parity with Coralogix RUM's own session model (cx_rum,
 * currently disabled): that SDK persists a session across tab closes on an
 * inactivity timeout and links consecutive sessions via `prev_session`.
 * Replicating that would need its own timeout/linking logic and real
 * cross-tab coordination (a shared localStorage key risks two tabs racing
 * to create the first session id) — out of scope here. This id exists to
 * correlate spans/metrics within one browsing session, not to match cx_rum
 * byte-for-byte.
 *
 * Never throws: falls back to a fixed placeholder if sessionStorage or
 * crypto.randomUUID are unavailable (e.g. private browsing, insecure
 * context) — every session on that path shares one placeholder value
 * rather than the setup step failing.
 */
const getOrCreateSessionId = (): string => {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch (error) {
    reportSetupFailure('session-id', error);
    return 'unavailable';
  }
};

type BrowserName = 'Chrome' | 'Edge' | 'Opera' | 'Firefox' | 'Safari' | 'Unknown';
type OsName = 'Windows' | 'macOS' | 'iOS' | 'Android' | 'Linux' | 'Unknown';

interface ParsedUserAgent {
  readonly browserName: BrowserName;
  readonly browserVersion: string;
  readonly osName: OsName;
  readonly osVersion: string;
}

/**
 * Minimal, dependency-free User-Agent parser (no ua-parser-js — kept
 * self-contained). Order matters: Chromium Edge/Opera UAs also contain
 * "Chrome/", and iOS Chrome/Firefox ("CriOS/"/"FxiOS/") plus virtually every
 * WebKit/Chromium browser carry a legacy "Safari/537.36" compatibility
 * token — so the specific tokens must be checked before the generic
 * Chrome/Safari fallbacks, and Safari must be the last, most-generic check.
 * Pure string matching — cannot throw, so callers don't need to guard it.
 */
const parseUserAgent = (userAgent: string): ParsedUserAgent => {
  const capture = (pattern: RegExp): string => pattern.exec(userAgent)?.[1] ?? 'unknown';

  let browserName: BrowserName = 'Unknown';
  let browserVersion = 'unknown';
  if (/Edg\//.test(userAgent)) {
    browserName = 'Edge';
    browserVersion = capture(/Edg\/([\d.]+)/);
  } else if (/OPR\//.test(userAgent)) {
    browserName = 'Opera';
    browserVersion = capture(/OPR\/([\d.]+)/);
  } else if (/FxiOS\//.test(userAgent)) {
    browserName = 'Firefox';
    browserVersion = capture(/FxiOS\/([\d.]+)/);
  } else if (/CriOS\//.test(userAgent)) {
    browserName = 'Chrome';
    browserVersion = capture(/CriOS\/([\d.]+)/);
  } else if (/Firefox\//.test(userAgent)) {
    browserName = 'Firefox';
    browserVersion = capture(/Firefox\/([\d.]+)/);
  } else if (/Chrome\//.test(userAgent)) {
    browserName = 'Chrome';
    browserVersion = capture(/Chrome\/([\d.]+)/);
  } else if (/Version\/[\d.]+.*Safari\//.test(userAgent)) {
    browserName = 'Safari';
    browserVersion = capture(/Version\/([\d.]+)/);
  }

  let osName: OsName = 'Unknown';
  let osVersion = 'unknown';
  if (/Windows NT [\d.]+/.test(userAgent)) {
    osName = 'Windows';
    osVersion = capture(/Windows NT ([\d.]+)/);
  } else if (/(iPhone|iPad|iPod).*OS [\d_]+/.test(userAgent)) {
    osName = 'iOS';
    osVersion = capture(/OS ([\d_]+)/).replace(/_/g, '.');
  } else if (/Mac OS X [\d_.]+/.test(userAgent)) {
    osName = 'macOS';
    osVersion = capture(/Mac OS X ([\d_.]+)/).replace(/_/g, '.');
  } else if (/Android [\d.]+/.test(userAgent)) {
    osName = 'Android';
    osVersion = capture(/Android ([\d.]+)/);
  } else if (/Linux/.test(userAgent)) {
    osName = 'Linux';
  }

  return { browserName, browserVersion, osName, osVersion };
};

/** Current SPA route, kept in sync by `setupPageTracking`. `'unknown'` until the first update. */
let currentPagePath = 'unknown';

const UUID_PATH_SEGMENT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMERIC_PATH_SEGMENT = /\/\d+(?=\/|$)/g;

/**
 * Collapses UUID/numeric path segments to `:id` so `page.path` stays a
 * bounded route *pattern* (e.g. "/leads/:id") instead of one distinct value
 * per record ever visited. Web Vitals ride OTel metrics, where an unbounded
 * set of path values becomes an unbounded set of metric label series, not
 * just log noise — this keeps it bounded to the app's actual route count.
 */
const normalizePagePath = (pathname: string): string =>
  pathname.replace(UUID_PATH_SEGMENT, ':id').replace(NUMERIC_PATH_SEGMENT, '/:id');

const updateCurrentPagePath = (): void => {
  currentPagePath = normalizePagePath(window.location.pathname);
};

/**
 * Tracks the current SPA route without importing the router (kept
 * self-contained in this file): patches `history.pushState`/`replaceState`
 * and listens for `popstate`, since this is a client-routed app and most
 * navigations never trigger a hard page load. With a data router
 * (createBrowserRouter), a route's loader can fetch before the URL commits
 * via pushState — a fetch span opened in that brief window is tagged with
 * the *previous* page, not the destination. Same inherent limitation every
 * history-patching RUM agent has (Coralogix RUM's own SDK included); not
 * something to "fix" here.
 */
const setupPageTracking = (): void => {
  updateCurrentPagePath();

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = (...args: Parameters<History['pushState']>): void => {
    originalPushState(...args);
    updateCurrentPagePath();
  };
  history.replaceState = (...args: Parameters<History['replaceState']>): void => {
    originalReplaceState(...args);
    updateCurrentPagePath();
  };

  window.addEventListener('popstate', updateCurrentPagePath);
};

/**
 * Tags every span with the current SPA page path. Runs on every span the
 * auto-instrumentations create (fetch/XHR/click/document-load) for the life
 * of the session — including inside the click handler or fetch call site
 * being instrumented, since `SpanProcessor.onStart` has no error boundary
 * of its own and an uncaught throw here would propagate into that
 * application code, not just drop a span. Session id / browser / OS don't
 * need this: they're static for the session and live on the `Resource`
 * instead, so every span *and* metric gets them for free at zero per-span
 * cost.
 */
class PageContextSpanProcessor implements SpanProcessor {
  onStart(span: Span, _parentContext: Context): void {
    try {
      span.setAttribute('page.path', currentPagePath);
    } catch (error) {
      reportSetupFailure('page-context-span-processor', error);
    }
  }

  onEnd(): void {}

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

const setupTraceProvider = (resource: Resource): void => {
  const traceProvider = new WebTracerProvider({
    resource,
    // `addSpanProcessor` is deprecated in favor of this constructor option.
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: `${env.otel.endpoint}/v1/traces` })),
      new PageContextSpanProcessor(),
    ],
  });
  traceProvider.register({ contextManager: new ZoneContextManager() });
};

const setupMeterProvider = (resource: Resource): void => {
  const meterProvider = new MeterProvider({
    resource,
    readers: [
      new PeriodicExportingMetricReader({
        exporter: new OTLPMetricExporter({ url: `${env.otel.endpoint}/v1/metrics` }),
        exportIntervalMillis: 30_000,
      }),
    ],
  });
  metrics.setGlobalMeterProvider(meterProvider);
};

const setupAutoInstrumentations = (): void => {
  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-document-load': {},
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls: [/.*/],
          clearTimingResources: true,
        },
        '@opentelemetry/instrumentation-xml-http-request': {
          propagateTraceHeaderCorsUrls: [/.*/],
        },
        '@opentelemetry/instrumentation-user-interaction': {},
      }),
    ],
  });
};

const setupWebVitalsMetrics = (): void => {
  const meter = metrics.getMeter('qfly-frontend-web-vitals');
  const lcpHistogram = meter.createHistogram('web_vitals.lcp', { unit: 'ms' });
  const clsHistogram = meter.createHistogram('web_vitals.cls');
  const inpHistogram = meter.createHistogram('web_vitals.inp', { unit: 'ms' });
  const ttfbHistogram = meter.createHistogram('web_vitals.ttfb', { unit: 'ms' });

  // web-vitals invokes this callback asynchronously, long after setup
  // returns — guard it independently so a bad reading never surfaces as an
  // unhandled exception on the page.
  const recordMetric = (histogram: ReturnType<typeof meter.createHistogram>) => (metric: Metric): void => {
    try {
      // `page.path` is dynamic (changes on SPA navigation) so it can't live
      // on the static Resource like session id/browser/OS — read the
      // tracked value at record time instead.
      histogram.record(metric.value, { 'web_vitals.rating': metric.rating, 'page.path': currentPagePath });
    } catch (error) {
      reportSetupFailure(`web-vitals.${metric.name}`, error);
    }
  };

  onLCP(recordMetric(lcpHistogram));
  onCLS(recordMetric(clsHistogram));
  onINP(recordMetric(inpHistogram));
  onTTFB(recordMetric(ttfbHistogram));
};

/** Normalizes a caught `error`/`reason` value into a `recordException`-compatible shape. */
const toRecordableException = (value: unknown): Exception =>
  value instanceof Error ? value : { message: String(value) };

type CapturedErrorType = 'uncaught' | 'unhandledrejection';

/**
 * Captures uncaught errors and unhandled promise rejections.
 *
 * Two records per error, matching Coralogix RUM's error instrumentation:
 * a `frontend.errors` counter (low-cardinality `error.type` only, safe for
 * rate-based alerting/dashboards) and a short-lived span carrying the full
 * exception detail (message/stack/filename/line/col) via `recordException`,
 * which rides the existing traces pipeline instead of needing a new one.
 */
const setupErrorCapture = (): void => {
  const errorCounter = metrics
    .getMeter('qfly-frontend-errors')
    .createCounter('frontend.errors', { description: 'Count of JavaScript errors captured in the browser' });
  const tracer = trace.getTracer('qfly-frontend-errors');

  const recordCapturedError = (
    value: unknown,
    errorType: CapturedErrorType,
    attributes: Record<string, string> = {},
  ): void => {
    try {
      errorCounter.add(1, { 'error.type': errorType });

      const span = tracer.startSpan(`error.${errorType}`);
      span.setAttributes(attributes);
      span.recordException(toRecordableException(value));
      span.setStatus({ code: SpanStatusCode.ERROR });
      span.end();
    } catch (error) {
      // Telemetry recording must never throw back into the global handlers
      // below — that would recurse into another 'error' event.
      reportSetupFailure(`error-capture.${errorType}`, error);
    }
  };

  window.addEventListener('error', (event) => {
    recordCapturedError(event.error ?? event.message, 'uncaught', {
      'error.filename': event.filename ?? '',
      'error.lineno': String(event.lineno ?? ''),
      'error.colno': String(event.colno ?? ''),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    recordCapturedError(event.reason, 'unhandledrejection');
  });
};

/** Never let a telemetry-setup failure crash app bootstrap — log and move on. */
const reportSetupFailure = (step: string, error: unknown): void => {
  console.warn(`[otel] failed to initialize "${step}"`, error);
};
